import { useQuery } from '@tanstack/react-query';
import { IPackageDto, IPackageFeeDto, IProductDto } from './dtos';
import { datahubAxiosClient, ENDPOINT_DETAILS, PACKAGES_ENDPOINT } from 'constants/server/apiEndPoint';
import { mapPackageFromDto, mapProductFromDto } from './helpers';
import { IPackage, IProduct } from './models';
import { ORDER_CREATION_PACKAGE_TYPE } from 'constants/ui/status';
import { MAX_RETRY_ATTEMPTS } from 'constants/server/apiSettings';

export const packageQueryKey = {
  all: ['packages'] as const,
  getPackages: (textSearch?: string) => ['getPackages', textSearch] as const,
  additional: (id: string) => ['additional', id] as const,
  emptyPackage: (id: string) => ['emptyPackage', id] as const,
  getFees: (id: string) => [...packageQueryKey.all, 'fees', id] as const,
};

type PackageParams = Partial<{
  branchId: string;
  textSearch: string;
  packageType: ORDER_CREATION_PACKAGE_TYPE;
  enabled: boolean;
}>;

export const getEmptyPackagesProductByBranchId = (params: PackageParams) => {
  const { branchId = '', enabled } = params;
  const { data, isLoading, isFetching, isError } = useQuery<IProduct[]>({
    queryKey: packageQueryKey.emptyPackage(branchId),
    queryFn: async () => {
      const response = await datahubAxiosClient.get<IProductDto[]>(ENDPOINT_DETAILS.getProductListByBranchId(branchId));
      return response.data?.map(mapProductFromDto) ?? [];
    },
    enabled: enabled,
  });

  return { data, isLoading, isFetching, isError };
};

export const getAdditionalProductsApi = (params: PackageParams) => {
  const { branchId = '', enabled } = params;
  const { data, isLoading, isFetching, isError } = useQuery<IProduct[]>({
    queryKey: packageQueryKey.additional(branchId),
    queryFn: async () => {
      const response = await datahubAxiosClient.get<IProductDto[]>(ENDPOINT_DETAILS.additionalProduct(branchId));
      return response.data?.map(mapProductFromDto) ?? [];
    },
    enabled: enabled,
  });

  return { data, isLoading, isFetching, isError };
};

export const getPackagesApi = (params: PackageParams) => {
  const { branchId, textSearch, packageType } = params;
  const { data, isLoading, isFetching, isError, isRefetching } = useQuery<IPackage[]>({
    queryKey: packageQueryKey.getPackages(textSearch),
    queryFn: async () => {
      const response = await datahubAxiosClient.post<IPackageDto[]>(PACKAGES_ENDPOINT, {
        branchId,
        searchString: textSearch,
        type: packageType,
      });

      return (
        response?.data
          ?.map(mapPackageFromDto)
          ?.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())) || []
      );
    },
    enabled: !!branchId,
  });

  return { data, isLoading, isFetching, isError, isRefetching };
};

export const useGetBranchesFeesApi = (branchId: string, isPickUpKeyChosen: boolean) => {
  const { data, isLoading, isFetching, isError } = useQuery<IPackageFeeDto[]>({
    queryKey: packageQueryKey.getFees(branchId),
    queryFn: async () => {
      const response = await datahubAxiosClient.get(ENDPOINT_DETAILS.getFees(branchId));

      return response?.data;
    },
    retry: MAX_RETRY_ATTEMPTS,
    enabled: !!branchId && !!isPickUpKeyChosen,
  });

  return { data, isLoading, isFetching, isError };
};
